import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Formik, FormikErrors } from "formik";
import {
  MembershipRequestApiClient,
  ResendEmailRequestType,
} from "../../api/apiClientMembershipRequest";
import { CherryPayApi } from "../../api/models";
import { useBusinessContext } from "../../context/ModelContext";
import { useApiClient } from "../../hooks/useApiClient";
import { useToast } from "../../hooks/useToast";
import { isValidEmail } from "../../util/isValidEmail";
import { isEmptyStr } from "../../util/StringUtil";
import { TextField } from "../fields/TextField/TextField";
import { FormStack } from "../FormStack/FormStack";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";

export type ResendConfirmationEmailRequestType = ResendEmailRequestType;

interface ResendConfirmationEmailModalProps {
  requestType: ResendConfirmationEmailRequestType;
  sessionId?: string;
  isOpen: boolean;
  onClose: () => void;
}

interface ResendConfirmationEmailFormValues {
  emailType: CherryPayApi.ResendEmailType | "";
  email: string;
}

export const ResendConfirmationEmailModal = ({
  requestType,
  sessionId,
  isOpen,
  onClose,
}: ResendConfirmationEmailModalProps) => {
  const apiClient = useApiClient();
  const business = useBusinessContext();
  const { showErrorToast, showSuccessToast } = useToast();
  const membershipRequestApiClient = useMemo(
    () => new MembershipRequestApiClient(apiClient.cherryPayClient),
    [apiClient]
  );

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [emailOptions, setEmailOptions] = useState<
    CherryPayApi.ResendEmailOption[]
  >([]);
  const [defaultEmail, setDefaultEmail] = useState("");

  // Load the emails that can be resent, and the email on file, each time the modal opens.
  useEffect(() => {
    if (!isOpen || !sessionId) {
      // Show the loading state (not a stale form) the next time it opens.
      setIsLoading(true);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setEmailOptions([]);
    setDefaultEmail("");

    membershipRequestApiClient
      .getResendEmailOptions(requestType, business.BusinessId, sessionId)
      .catch(() => ({ ok: false as const, message: "Unknown error." }))
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.ok) {
          setEmailOptions(result.data.Options ?? []);
          setDefaultEmail(result.data.EmailAddress ?? "");
        } else {
          setLoadError(result.message || "Unknown error.");
        }
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    sessionId,
    requestType,
    business.BusinessId,
    membershipRequestApiClient,
  ]);

  const enabledEmailTypes = useMemo(
    () =>
      emailOptions
        .filter((option) => option.Enabled)
        .map((option) => option.EmailType),
    [emailOptions]
  );

  const validate = ({
    email,
    emailType,
  }: ResendConfirmationEmailFormValues) => {
    const errors: FormikErrors<ResendConfirmationEmailFormValues> = {};
    if (!emailType || !enabledEmailTypes.includes(emailType)) {
      errors.emailType = "Select an email to send.";
    }
    if (isEmptyStr(email)) {
      errors.email = "Email address is required.";
    } else if (!isValidEmail(email.trim())) {
      errors.email = "Invalid email address.";
    }
    return errors;
  };

  // Synchronous guard: Formik's isSubmitting only disables the button after a re-render,
  // so a fast double click could POST (and send the email) twice.
  const isSendingRef = useRef(false);

  const onSubmit = async ({
    email,
    emailType,
  }: ResendConfirmationEmailFormValues) => {
    if (!sessionId || !emailType || isSendingRef.current) {
      return;
    }
    isSendingRef.current = true;
    const emailAddress = email.trim();
    let result;
    try {
      result = await membershipRequestApiClient.resendEmail(
        requestType,
        business.BusinessId,
        sessionId,
        emailType,
        emailAddress
      );
    } finally {
      isSendingRef.current = false;
    }

    if (result.ok) {
      const displayName =
        emailOptions.find((option) => option.EmailType === emailType)
          ?.DisplayName ?? "Email";
      showSuccessToast(`${displayName} sent to ${emailAddress}`);
      onClose();
    } else {
      showErrorToast(result.message || "Failed to send email");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Resend Email</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6">
          {isLoading ? (
            <LoadingIndicator color="#333" />
          ) : loadError ? (
            <VStack alignItems="stretch" spacing="4">
              <ErrorMessage>
                Could not load the emails for this request: {loadError}
              </ErrorMessage>
              <HStack justifyContent="end">
                <Button onClick={onClose}>Close</Button>
              </HStack>
            </VStack>
          ) : (
            <Formik<ResendConfirmationEmailFormValues>
              initialValues={{
                emailType: enabledEmailTypes[0] ?? "",
                email: defaultEmail,
              }}
              validate={validate}
              validateOnMount
              onSubmit={onSubmit}
            >
              {({ isSubmitting, isValid, values, errors, setFieldValue }) => (
                <FormStack>
                  <FormControl as="fieldset">
                    <FormLabel as="legend">Email to send</FormLabel>
                    <RadioGroup
                      name="emailType"
                      value={values.emailType}
                      onChange={(value) => setFieldValue("emailType", value)}
                    >
                      <VStack alignItems="flex-start" spacing="3">
                        {emailOptions.map((option) => (
                          <VStack
                            key={option.EmailType}
                            alignItems="flex-start"
                            spacing="0"
                          >
                            <Radio
                              value={option.EmailType}
                              isDisabled={!option.Enabled || isSubmitting}
                            >
                              {option.DisplayName}
                            </Radio>
                            {!option.Enabled && option.DisabledReason && (
                              <Text fontSize="sm" color="gray.500" pl="6">
                                {option.DisabledReason}
                              </Text>
                            )}
                          </VStack>
                        ))}
                      </VStack>
                    </RadioGroup>
                  </FormControl>
                  {enabledEmailTypes.length === 0 && (
                    <ErrorMessage>
                      None of these emails can be sent for this request.
                    </ErrorMessage>
                  )}
                  <TextField
                    name="email"
                    label="Email address"
                    placeholder="Email address"
                    isValid={!!values.email && !errors.email}
                  />
                  <HStack width="100%" justifyContent="end" spacing="3" pt="4">
                    <Button
                      isLoading={isSubmitting}
                      colorScheme="cherryButton"
                      color="#fff"
                      type="submit"
                      disabled={isSubmitting || !isValid}
                    >
                      Send Email
                    </Button>
                    <Button disabled={isSubmitting} onClick={onClose}>
                      Cancel
                    </Button>
                  </HStack>
                </FormStack>
              )}
            </Formik>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
